import React from "react";
import { Form, Button, Dropdown } from "semantic-ui-react";
import { useFormik } from "formik";
import { BasicModal } from "../../../Shared";
import { useLanguage } from "../../../../contexts";
import { determineAirEmissionsUnits } from "../../../../utils/dataAndFormulas/airemissionsForms";
import "./UnitsForm.scss";

export function UnitsForm(props) {
  const { show, onClose, onSave, currentUnits, registeredFields } = props;
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  // Todos los campos de air emissions con sus 38 campos
  const allAirEmissionsFields = [
    { key: "carbon_dioxide", label: t("carbon_dioxide") },
    { key: "carbon_monoxide", label: t("carbon_monoxide") },
    { key: "particulate_material", label: t("particulate_material") },
    { key: "volatile_organic_compounds", label: t("volatile_organic_compounds") },
    { key: "sulfur_oxides", label: t("sulfur_oxides") },
    { key: "nitrogen_oxides", label: t("nitrogen_oxides") },
    { key: "chlorofluorocarbons", label: t("chlorofluorocarbons") },
    { key: "hydrochlorofluorocarbons", label: t("hydrochlorofluorocarbons") },
    { key: "hydrofluorocarbons", label: t("hydrofluorocarbons") },
    { key: "hydrofluoroolefins", label: t("hydrofluoroolefins") },
    { key: "perfluorocarbons", label: t("perfluorocarbons") },
    { key: "substances_exhaust_ozone_layer", label: t("substances_exhaust_ozone_layer") },
    { key: "ozone", label: t("ozone") },
    { key: "toxic_or_dangerous_atmospheric_pollutants", label: t("toxic_or_dangerous_atmospheric_pollutants") },
    { key: "ammonia", label: t("ammonia") },
    { key: "propane", label: t("propane") },
    { key: "isobutane", label: t("isobutane") },
    { key: "propylene", label: t("propylene") },
    { key: "ethylene", label: t("ethylene") },
    { key: "hexafluoroethane", label: t("hexafluoroethane") },
    { key: "octafluoropropane", label: t("octafluoropropane") },
    { key: "chlorotrifluoromethane", label: t("chlorotrifluoromethane") },
    { key: "chlorodifluoromethane", label: t("chlorodifluoromethane") },
    { key: "tetrafluoroethane", label: t("tetrafluoroethane") },
    { key: "trifluoromethane", label: t("trifluoromethane") },
    { key: "pentafluoroethane", label: t("pentafluoroethane") },
    { key: "hexafluoropropylene", label: t("hexafluoropropylene") },
    { key: "hydrogen", label: t("hydrogen") },
    { key: "methane", label: t("methane") },
    { key: "oxygen", label: t("oxygen") },
    { key: "nitrogen", label: t("nitrogen") },
    { key: "hydrogen_sulfide", label: t("hydrogen_sulfide") },
    { key: "carbon_disulfide", label: t("carbon_disulfide") },
    { key: "nitrous_oxide", label: t("nitrous_oxide") },
    { key: "hydrochlorofluorocarbons_ref", label: t("hydrochlorofluorocarbons_ref") },
    { key: "hydrofluorocarbons_ref", label: t("hydrofluorocarbons_ref") },
    { key: "hydrofluoroolefins_ref", label: t("hydrofluoroolefins_ref") },
    { key: "perfluorocarbonos_ref", label: t("perfluorocarbonos_ref") },
  ];

  // Si hay campos registrados en el Form, usar solo los activos; si no, usar todos
  const airEmissionsFields =
    registeredFields && registeredFields.length > 0
      ? registeredFields
          .filter((field) => field.active !== false)
          .map((field) => ({
            key: field.name,
            label: t(field.name),
          }))
      : allAirEmissionsFields;

  // Debug: Ver qué opciones genera determineAirEmissionsUnits
  React.useEffect(() => {
    if (airEmissionsFields.length > 0) {
      const firstField = airEmissionsFields[0].key;
      const options = determineAirEmissionsUnits(firstField);
      // console.log(
      //   "🔍 AirEmissions UnitsForm - Opciones disponibles para",
      //   firstField,
      //   ":",
      //   options
      // );
      // console.log("🔍 AirEmissions UnitsForm - airEmissionsFields:", airEmissionsFields);
      // console.log("🔍 AirEmissions UnitsForm - currentUnits:", currentUnits);
    }
  }, [airEmissionsFields, currentUnits]);

  const formik = useFormik({
    initialValues: airEmissionsFields.reduce((acc, field) => {
      // Si ya existe una unidad guardada, usarla
      if (currentUnits?.[field.key]) {
        acc[field.key] = currentUnits[field.key];
      } else {
        // Si no hay unidad guardada, obtener las opciones disponibles
        const availableUnits = determineAirEmissionsUnits(field.key);
        // Si solo hay una opción, asignarla automáticamente
        if (availableUnits.length === 1) {
          acc[field.key] = availableUnits[0].value;
        } else {
          acc[field.key] = "";
        }
      }
      return acc;
    }, {}),
    enableReinitialize: true,
    onSubmit: (values) => {
      // console.log("📝 AirEmissions UnitsForm - Valores del formulario al guardar:", values);
      // console.log("📝 AirEmissions UnitsForm - currentUnits recibidos:", currentUnits);
      onSave(values);
      onClose();
    },
  });

  return (
    <BasicModal
      show={show}
      close={onClose}
      size="large"
      className="units-form-modal"
    >
      <Form onSubmit={formik.handleSubmit} className="units-form-modal">
        <div className="units-grid">
          {airEmissionsFields.map((field) => (
            <div key={field.key} className="unit-row">
              <span className="unit-label">{field.label}</span>
              <Dropdown
                placeholder={t("select_unit")}
                fluid
                selection
                search
                clearable
                options={determineAirEmissionsUnits(field.key).map((u) => ({
                  key: u.key,
                  value: u.value,
                  text: t(u.name),
                }))}
                value={formik.values[field.key]}
                onChange={(_, data) =>
                  formik.setFieldValue(field.key, data.value)
                }
              />
            </div>
          ))}
        </div>

        <div style={{ marginTop: "20px", display: "flex", justifyContent: "center", gap: "8px" }}>
           <Button type="submit" primary>
            {t("save")}
          </Button>
          <Button type="button" onClick={onClose} className="units-btn-cancel">
            {t("cancel")}
          </Button>
         
        </div>
      </Form>
    </BasicModal>
  );
}
